import React from "react";
import { AlertCircle, Zap, ChevronRight } from "lucide-react";

/**
 * Phase 2H — Next Best Action chip.
 * Uses the OrangeBySite palette (orange/black/white/grey) with a restrained
 * red reserved for URGENT financial action.
 */

const TONE = {
  URGENT: { fg: "#C1350B", bg: "#FFECEC", bd: "#FCE1C7", dot: "🔴" },
  HIGH:   { fg: "#8A2E00", bg: "#FFF7EE", bd: "#FCE1C7", dot: "🟠" },
  MEDIUM: { fg: "#8A6D00", bg: "#FFF9E6", bd: "#F5E4B0", dot: "🍊" },
  LOW:    { fg: "#4A4A4A", bg: "#F6F5F2", bd: "#EAEAEA", dot: "↻" },
};

// Phase 3I.4P.3.3 — SCAN_WEBSITE gets a lightning-bolt affordance so
// operators immediately recognise "no score yet — click to scan".
const SCAN_TONE = { fg: "#8A2E00", bg: "#FFF7EE", bd: "#FCE1C7" };

export default function NudgeChip({ nudge, onClick, compact = true, testId }) {
  if (!nudge) {
    return (
      <span data-testid={testId || "hq-nudge-empty"}
            className="text-[#8A8A8A] text-[12px]">—</span>
    );
  }
  if (nudge.type === "SCAN_WEBSITE") {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid={testId || "hq-nudge-SCAN_WEBSITE"}
        title={nudge.reason || nudge.label}
        className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-black tracking-[0.06em] uppercase whitespace-nowrap hover:opacity-90"
        style={{ color: SCAN_TONE.fg, background: SCAN_TONE.bg, border: `1px solid ${SCAN_TONE.bd}` }}
      >
        <Zap size={11} className="text-[#FF6A00]"/>
        {nudge.label || "SCAN WEBSITE"}
      </button>
    );
  }
  const tone = TONE[nudge.priority] || TONE.MEDIUM;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId || `hq-nudge-${nudge.type}`}
      title={nudge.reason || nudge.label}
      className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] font-black tracking-[0.06em] uppercase whitespace-nowrap hover:opacity-90"
      style={{ color: tone.fg, background: tone.bg, border: `1px solid ${tone.bd}` }}
    >
      <span aria-hidden>{tone.dot}</span>
      {compact ? nudge.label : (
        <>
          {nudge.label}
          <ChevronRight size={11}/>
        </>
      )}
    </button>
  );
}

export const NUDGE_FILTER_OPTIONS = [
  { value: "",                    label: "All actions"       },
  { value: "URGENT",              label: "🔴 Urgent"          },
  { value: "HIGH",                label: "🟠 High priority"   },
  { value: "SALES_OPPORTUNITY",   label: "🍊 Sales opportunity" },
  { value: "PAYMENT",             label: "💸 Payment"        },
  { value: "NO_ACTION",           label: "No action"         },
];

// Routes each nudge type to the correct existing tab/action on the profile.
export function nudgeTabForAction(action) {
  return {
    invoices: "invoices",
    leads:    "leads",
    subs:     "subs",
    services: "services",
    preview:  "overview",
    rescan:   "overview",
  }[action] || "overview";
}
